<script setup lang="ts">
import {computed} from "vue";
import {DropDownList} from "@progress/kendo-vue-dropdowns";
import {useUxData} from "@/stores/ux-data";
import {useAppDataStore} from "@/stores/app-data";

const emit = defineEmits(['attributeSelected'])
const props = withDefaults(
    defineProps<{
      id?: string | undefined,
      currentValue?: string | undefined,
      includeDerived?: boolean | undefined,
      defaultItem?: { label: string | null, key: string | null } | undefined,
      // Optional controlled selection (the attribute key, or null for none).
      // Separate from the legacy `currentValue` prop (which is intentionally
      // left unwired to avoid changing existing consumers). Omitted → uncontrolled.
      selectedKey?: string | null,
    }>(),
    {
      includeDerived: false
    }
)

const store = useAppDataStore();
const ux = useUxData();

// Memoized so the item bound as `:value` is the SAME object reference as its
// entry in `:data-items` (both read from this one computed), which the Kendo
// DropDownList relies on to match the selected value against its options.
const dataItems = computed(() => {
  const attributes = ux.labels?.swade?.attributes ?? {};
  const derived = attributes.derived ?? {};
  // Labels fall back to the attribute key so a missing label subtree never yields
  // an `undefined` label (which the Kendo DropDownList would .toString() and crash on).
  return [
    { label: attributes.agility ?? 'agility', key: 'agility' },
    { label: attributes.smarts ?? 'smarts', key: 'smarts' },
    { label: attributes.spirit ?? 'spirit', key: 'spirit' },
    { label: attributes.strength ?? 'strength', key: 'strength' },
    { label: attributes.vigor ?? 'vigor', key: 'vigor' },
    { label: derived.pace ?? 'pace', key: 'pace' },
    { label: derived.runningDie ?? 'runningDie', key: 'runningDie' },
    { label: derived.parry ?? 'parry', key: 'parry' },
    { label: derived.toughness ?? 'toughness', key: 'toughness' },
    { label: derived.armor ?? 'armor', key: 'armor' },
    { label: derived.size ?? 'size', key: 'size' },
    { label: derived.strain ?? 'strain', key: 'strain' },
  ].filter((item: any) => {
    return props.includeDerived || !['pace','parry','toughness','armor','size','runningDie','strain'].includes(item.key);
  })
})

const valueBinding = computed(() =>
  props.selectedKey === undefined
    ? {}
    : { value: dataItems.value.find((item: any) => item.key === props.selectedKey) ?? null }
)

const onChange = (event: any) => {
  emit('attributeSelected', event);
}
</script>

<template>
  <DropDownList
      :id="id"
      text-field="label"
      :default-item="defaultItem"
      :data-items="dataItems"
      v-bind="valueBinding"
      @change="onChange"
  >
  </DropDownList>
</template>
